import { ReactNode } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { FullPageSkeleton } from "@/components/ui/feedback/FullPageSkeleton";
import Landing from "@/pages/Landing";

/**
 * Root route handler ("/").
 * - Loading → show loading (block routing until auth resolves)
 * - Unauthenticated → show Landing page
 * - Authenticated but onboarding incomplete → /onboarding
 * - Authenticated + onboarding complete → /today
 * 
 * CRITICAL RULES:
 * 1. Never show onboarding when status !== "authenticated"
 * 2. Never show onboarding when user === null
 * 3. Onboarding is ONLY shown when: status === "authenticated" && user.onboarding_complete === false
 * 4. Block all routing until auth resolves (status !== "loading")
 * 
 * NOTE: Backend should set HTTP-only cookies during OAuth callback.
 * AuthContext automatically checks authentication via /me endpoint.
 */
export function AuthLanding() {
  const { user, loading, status, authReady } = useAuth();

  // CRITICAL: Hard gate - block all routing until auth status is resolved
  // This prevents redirect loops on refresh/deep-link
  // Use authReady to ensure we never redirect during bootstrap
  if (!authReady || status === "bootstrapping" || loading) {
    return <FullPageSkeleton />;
  }

  // CRITICAL: Unauthenticated → show Landing page (NOT redirect to login)
  // Users can navigate to /login or /signup from the landing page
  // Only check status, not user - status is the source of truth
  // Checking !user can cause race conditions during hydration
  if (status === "unauthenticated") {
    return <Landing />;
  }

  // CRITICAL: Only show onboarding when authenticated AND onboarding incomplete
  // Onboarding is a post-auth state, not an auth fallback
  if (status === "authenticated" && user && !user.onboarding_complete) {
    return <Navigate to="/onboarding" replace />;
  }

  // Authenticated + onboarding complete → today
  return <Navigate to="/today" replace />;
}

/**
 * Wrapper for public-only pages like /login and /signup.
 * Prevents showing auth forms to already-authenticated users.
 * 
 * CRITICAL: Only redirect to onboarding when authenticated AND onboarding incomplete
 */
export function PublicOnly({ children }: { children: ReactNode }) {
  const { user, loading, status, authReady } = useAuth();

  // CRITICAL: Block routing until auth resolves
  // Use authReady to ensure we never redirect during bootstrap
  if (!authReady || status === "bootstrapping" || loading) {
    return <FullPageSkeleton />;
  }

  // CRITICAL: Only redirect to onboarding when authenticated AND onboarding incomplete
  // Onboarding is a post-auth state, not an auth fallback
  // CRITICAL: Only check authenticated status, never redirect unauthenticated users
  if (status === "authenticated" && user) {
    if (!user.onboarding_complete) {
      return <Navigate to="/onboarding" replace />;
    }
    return <Navigate to="/today" replace />;
  }

  // Unauthenticated users can access public routes (login/signup)
  return <>{children}</>;
}
